<template>
  <v-dialog v-model="show" max-width="1400px" scrollable>
    <v-card v-if="konto">
      <v-card-title class="py-2 kartica-naslov">
        <span class="konto-sifra">{{ konto.account4 }}</span>
        <span class="konto-naziv ml-2">{{ konto.account4Name }}</span>
        <span class="konto-meta ml-4">{{ podnaslov }}</span>
        <v-spacer></v-spacer>
        <v-btn small outlined color="primary" class="mr-2" :disabled="loading" @click="ispisi">
          Printaj
        </v-btn>
        <v-btn small outlined class="mr-2" :disabled="loading" @click="uExcel">
          Excel
        </v-btn>
        <v-btn icon @click="show = false"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="pa-2" style="max-height: 70vh;">
        <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

        <v-data-table
          dense
          class="kartica-tbl"
          :headers="headers"
          :items="rows"
          :items-per-page="-1"
          hide-default-footer
          no-data-text="Nema knjiženja na ovom kontu u razdoblju."
        >
          <template v-slot:[`body.prepend`]>
            <tr class="red-ps">
              <td colspan="5">Početno stanje</td>
              <td class="text-right">{{ novac(psDebit) }}</td>
              <td class="text-right">{{ novac(psCredit) }}</td>
              <td class="text-right">{{ novac(psDebit - psCredit) }}</td>
            </tr>
          </template>

          <template v-slot:[`item.BookingDate`]="{ item }">{{ localDate(item.BookingDate) }}</template>
          <template v-slot:[`item.Debit`]="{ item }">
            <span v-if="item.Debit">{{ novac(item.Debit) }}</span>
          </template>
          <template v-slot:[`item.Credit`]="{ item }">
            <span v-if="item.Credit">{{ novac(item.Credit) }}</span>
          </template>
          <template v-slot:[`item.saldo`]="{ item }">
            <span :class="item.saldo < 0 ? 'crveno' : ''">{{ novac(item.saldo) }}</span>
          </template>

          <template v-slot:[`body.append`]>
            <tr class="red-promet">
              <td colspan="5">Promet u razdoblju</td>
              <td class="text-right">{{ novac(prometD) }}</td>
              <td class="text-right">{{ novac(prometP) }}</td>
              <td class="text-right">{{ novac(prometD - prometP) }}</td>
            </tr>
            <tr class="red-ukupno">
              <td colspan="5">Ukupno</td>
              <td class="text-right">{{ novac(psDebit + prometD) }}</td>
              <td class="text-right">{{ novac(psCredit + prometP) }}</td>
              <td class="text-right">{{ novac((psDebit + prometD) - (psCredit + prometP)) }}</td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-snackbar v-model="snackbar" bottom :timeout="4000" color="red">{{ greska }}</v-snackbar>
  </v-dialog>
</template>

<script>
// Kartica konta - sva knjizenja jednog konta u razdoblju, najstarija prva, sa
// saldom koji se vodi niz listu.
//
// Otvara se preko ref-a:
//   <MainLedgerAccountCard ref="kartica" />
//   this.$refs.kartica.open({account4: '1200', account4Name: 'Kupci', psDebit, psCredit, head})
//
// Koristi postojeci /mainledgerdetailcard s type 2. Taj upit filtrira
// account4 BETWEEN fromAccount AND toAccount, a partnera dodaje samo ako je
// poslan - pa bez partnera vraca sva knjizenja konta.
//
// Pocetno stanje se NE dohvaca ponovno: MainLedgerCard1 ga je vec izracunao iz
// psItems, pa dolazi kao psDebit/psCredit i kartica salje samo jedan zahtjev.

import MainLedgerDetailService from '@/services/MainLedgerDetailService'
import dateFormat from '@/snippets/dateFormat'
import {printAccountCardPdf} from './mainLedgerPdf'
import {exportAccountCardXlsx} from './mainLedgerXlsx'

// Temeljnica pocetnog stanja. Njena knjizenja se izbacuju iz liste jer pocetno
// stanje vec stoji kao prvi redak - inace bi se zbrojilo dvaput i saldo koji se
// vodi niz listu bio bi kriv.
const KNJIGA_POCETNO_STANJE = 21

const num = (v) => {
  const n = parseFloat(v)
  return isNaN(n) ? 0 : n
}

export default {
  name: 'MainLedgerAccountCard',
  data () {
    return {
      show: false,
      loading: false,
      konto: null,
      rows: [],
      psDebit: 0,
      psCredit: 0,
      fromDate: null,
      toDate: null,
      currency: 'EUR',
      snackbar: false,
      greska: ''
    }
  },
  computed: {
    headers () {
      return [
        {text: 'Datum knjiž.', value: 'BookingDate', width: '110px', sortable: false},
        {text: 'Dokument', value: 'dokument', width: '140px', sortable: false},
        {text: 'Veza', value: 'veza', width: '130px', sortable: false},
        {text: 'Partner', value: 'partnerName', sortable: false},
        {text: 'Opis', value: 'Description', sortable: false},
        {text: 'Duguje', value: 'Debit', align: 'end', width: '120px', sortable: false},
        {text: 'Potražuje', value: 'Credit', align: 'end', width: '120px', sortable: false},
        {text: 'Saldo', value: 'saldo', align: 'end', width: '130px', sortable: false}
      ]
    },
    // Promet se zbraja iz stavaka koje kartica stvarno pokazuje, a ne preuzima
    // iz bilance - tako ono sto se vidi uvijek zbraja u ono sto pise dolje.
    prometD () {
      return this.rows.reduce((a, r) => a + num(r.Debit), 0)
    },
    prometP () {
      return this.rows.reduce((a, r) => a + num(r.Credit), 0)
    },
    podnaslov () {
      return [
        this.localDate(this.fromDate) + ' - ' + this.localDate(this.toDate),
        this.currency,
        this.rows.length + ' stavaka'
      ].join(' · ')
    }
  },
  methods: {
    novac (v) {
      return num(v).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    localDate (d) {
      return d ? dateFormat(d) : ''
    },
    // Ime dokumenta po istoj logici koju koristi kartica partnera.
    dokument (item) {
      const book = item.BookName ? String(item.BookName) : ''
      if (book === 'BS') {
        return 'Izv.' + (item.BookID ? parseInt(item.BookID) : '')
      }
      return (book + ' ' + (item.BookID ? parseInt(item.BookID) : '')).trim()
    },
    veza (item) {
      const inNr = item.invoiceInNumber ? String(item.invoiceInNumber).trim() : ''
      const outNr = item.invoiceOutNumber ? String(item.invoiceOutNumber).trim() : ''
      const book = item.BookName ? String(item.BookName).substring(0, 3) : ''
      let broj = ''
      if (book === 'URA') { broj = inNr }
      if (book === 'IRA') { broj = outNr }
      if (!broj) { broj = outNr || inNr }
      if (!broj) { return '' }
      return broj + (item.BusinessYearInvoice ? '/' + item.BusinessYearInvoice : '')
    },

    /**
     * @param {Object} p
     *   account4       obavezno, sifra konta
     *   account4Name   naziv konta, samo za zaglavlje
     *   psDebit, psCredit          pocetno stanje iz bilance
     *   head           mainledgerAna iz analize (CompanyId, BusinessYear, datumi, TypeID)
     *   exRate, currency
     */
    async open (p) {
      const account4 = p && p.account4 ? String(p.account4).trim() : ''
      if (!account4) {
        this.greska = 'Konto nema šifru, kartica se ne može otvoriti.'
        this.snackbar = true
        return
      }
      const head = p.head || {}
      const godina = head.BusinessYear || this.$store.state.businessYear

      this.konto = {account4: account4, account4Name: p.account4Name || ''}
      this.psDebit = num(p.psDebit)
      this.psCredit = num(p.psCredit)
      this.currency = p.currency || 'EUR'
      this.fromDate = head.docFromDate || (godina + '-01-01')
      this.toDate = head.docToDate || (godina + '-12-31')
      this.rows = []
      this.show = true
      this.loading = true

      // Partner se namjerno ne salje - kartica konta je cijeli konto, sa svim
      // partnerima. Vrsta knjige i projekt se prenose jer je i bilanca njima
      // filtrirana, pa bi se inace kartica i redak bilance razisli.
      const upit = {
        CompanyId: head.CompanyId || this.$store.state.companyid,
        BusinessYear: godina,
        docFromDate: this.fromDate,
        docToDate: this.toDate,
        fromAccount: account4,
        toAccount: account4,
        type: 2
      }
      if (head.TypeID) { upit.TypeID = head.TypeID }
      if (head.bookTypeSelected) { upit.bookTypeSelected = head.bookTypeSelected }

      try {
        const {data} = await MainLedgerDetailService.card(upit)
        const sve = data && data.documents ? data.documents : []
        // Bez temeljnice pocetnog stanja - ono je vec prvi redak kartice.
        const stavke = sve.filter(item => parseInt(item.BookTypeID) !== KNJIGA_POCETNO_STANJE)
        const exRate = num(p.exRate) || 1
        let saldo = this.psDebit - this.psCredit

        this.rows = stavke.map((item, i) => {
          const d = num(item.Debit) / exRate
          const c = num(item.Credit) / exRate
          saldo += d - c
          return Object.assign({}, item, {
            rb: i + 1,
            Debit: d,
            Credit: c,
            saldo: saldo,
            dokument: this.dokument(item),
            veza: this.veza(item),
            partnerName: item.partnerName || ''
          })
        })
      } catch (error) {
        this.greska = 'Greška kod čitanja kartice konta ' + account4
        this.snackbar = true
      }
      this.loading = false
    },
    uExcel () {
      exportAccountCardXlsx(this.zaIzvoz())
    },
    ispisi () {
      printAccountCardPdf(this.zaIzvoz())
    },
    zaIzvoz () {
      return {
        konto: this.konto,
        stavke: this.rows,
        psDebit: this.psDebit,
        psCredit: this.psCredit,
        prometDebit: this.prometD,
        prometCredit: this.prometP,
        company: this.$store.state.companyActiv ? this.$store.state.companyActiv : {},
        fromDate: this.fromDate,
        toDate: this.toDate,
        valuta: this.currency
      }
    }
  }
}
</script>

<style scoped>
.kartica-naslov { font-size: 16px; }
.konto-sifra { font-weight: 700; font-family: monospace; font-size: 18px; }
.konto-naziv { font-weight: 600; }
.konto-meta { font-size: 14px; color: rgba(0, 0, 0, .55); }

.kartica-tbl >>> table { font-size: 14px; }
.kartica-tbl >>> th { font-size: 13px !important; white-space: nowrap; }
.kartica-tbl >>> td { font-size: 14px !important; padding: 0 8px !important; height: 26px !important; }

.red-ps td {
  background: #eef4f2;
  font-weight: 600;
  font-size: 14px;
  padding: 0 8px;
  height: 28px;
  border-bottom: 1px solid rgba(0, 0, 0, .12);
}
.red-promet td,
.red-ukupno td {
  font-weight: 600;
  font-size: 14px;
  padding: 0 8px;
  height: 28px;
  background: #fafafa;
}
.red-promet td { border-top: 1px solid rgba(0, 0, 0, .25); }
.red-ukupno td { border-top: 2px solid #14534c; }

.crveno { color: #8c2f1e; }

.upozorenje {
  font-size: 12px;
  color: #8c2f1e;
  border: 1px solid #8c2f1e;
  border-radius: 2px;
  padding: 6px 10px;
}
</style>
